import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, RadioGroup } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { NameOption } from '../../nameOptions';
import type { PickerKey } from '../../useNamesStep';

type NameCandidatesProps = {
    /** The name as the file has it. */
    text: string;
    candidates: NameOption[];
    /** The picked entity's id, if any. */
    value: string | null;
    onChange: (key: PickerKey) => void;
};

/**
 * An ambiguous name's candidates: the entities that share the name, one
 * radio each, side by side and wrapping. Picking one is the only way to
 * settle the name. `mt-0` drops the group's top margin on each radio, as the
 * Branch step does.
 */
export const NameCandidates: React.FC<NameCandidatesProps> = ({
    text,
    candidates,
    value,
    onChange,
}) => {
    const { t } = useTranslation('pages');

    return (
        <RadioGroup
            aria-label={t('catalogImport.names.candidates.aria', { text })}
            className="flex-row flex-wrap gap-x-4 gap-y-2"
            onChange={onChange}
            orientation="horizontal"
            value={value}
            {...getNodeTestIdProp('catalog-import-names-candidates')}
        >
            {candidates.map((candidate) => (
                <Radio
                    className="mt-0"
                    key={candidate.id}
                    value={candidate.id}
                    {...getNodeTestIdProp(
                        `catalog-import-names-candidate-${candidate.id}`,
                    )}
                >
                    <Radio.Content className="flex-row items-center gap-2">
                        <Radio.Control>
                            <Radio.Indicator />
                        </Radio.Control>
                        {candidate.label}
                    </Radio.Content>
                </Radio>
            ))}
        </RadioGroup>
    );
};
